import {
  Box,
  CircularProgress,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import Permission from 'components/ValidPermission';
import TableItems, { TableCell, TableRow } from 'components/TableItems';
import i18n from 'i18n';
import { getLicenseGroupDetailsRequest } from 'modules/environments/actions';
import { modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import { LicenseGroupsFields } from 'modules/environments/interfaces';
import ApplicationState from 'store/interfaces';
import palette from 'theme/palette';
import { dateFormatWithAt } from 'utils/helpers';
import Button from 'components/Button';
import { Query } from 'hooks/useTableFilters';
import styles from './styles';
import FormLicenseGroups from './FormLicenseGroups';
import EditClients from './EditClients';

const useStyles = makeStyles(styles);

interface Props {
  uuid: string;
  isLoadingLicenseGroups: boolean;
  licenseGroupDetails: LicenseGroupsFields;
  queryGroups: Query;
  getLicenseGroupDetailsRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
}

const DetailsLicenseGroup = ({
  uuid,
  isLoadingLicenseGroups,
  licenseGroupDetails,
  queryGroups,
  getLicenseGroupDetailsRequest,
  modalOpen,
}: Props) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();

  React.useEffect(() => {
    getLicenseGroupDetailsRequest({
      environmentUuid: id,
      licenseGroupUuid: uuid,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid, id]);

  const headers = [
    {
      key: 'name',
      i18n: 'NAME_KEY',
    },
    {
      key: 'client_uuid',
      i18n: 'ID',
    },
  ];

  const handleEditGroup = () => {
    modalOpen({
      title: i18n.t('UPDATE_LICENSE_GROUP_KEY'),
      content: (
        <FormLicenseGroups
          queryGroups={queryGroups}
          initialValues={licenseGroupDetails}
          licenseLimitAvailable={
            licenseGroupDetails?.available_licenses_to_group
          }
        />
      ),
    });
  };

  const handleEditClientsModal = () => {
    modalOpen({
      title: i18n.t('EDIT_CLIENTS_IN_GROUP_KEY'),
      maxWidth: 'md',
      content: (
        <EditClients
          queryGroups={queryGroups}
          initialValues={licenseGroupDetails}
        />
      ),
    });
  };

  return isLoadingLicenseGroups ? (
    <Grid container justify="center">
      <CircularProgress size={15} />
    </Grid>
  ) : (
    <>
      <Grid
        container
        justify="space-between"
        className={classes.editButton}
        alignItems="center"
      >
        <Typography className={classes.titleSection}>
          {i18n.t('DETAILS_KEY')}
        </Typography>
        <Permission value="edit_license_group">
          <Button
            className={classes.button}
            outlineBlue
            onClick={() => handleEditGroup()}
          >
            {i18n.t('EDIT_GROUP_INFO_KEY')}
          </Button>
        </Permission>
      </Grid>
      <Grid container className={classes.detailCard}>
        <Grid container spacing={2}>
          <Typography className={classes.titleCard}>
            {licenseGroupDetails?.name}
          </Typography>
        </Grid>
        <Grid container className={classes.detailsGrid}>
          <Grid container>
            <Grid item xs={6}>
              <Typography className={classes.text} component="div">
                {`${i18n.t('BULK_CLIENTS.LICENSES_QUANTITY_KEY')}: `}
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {licenseGroupDetails?.license_limit}
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.text} component="div">
                {`${i18n.t('CREATED_AT_KEY')}: `}
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {dateFormatWithAt(licenseGroupDetails?.created_at)}
                </Box>
              </Typography>
            </Grid>
          </Grid>

          <Grid container>
            <Grid item xs={6}>
              <Typography className={classes.text} component="div">
                {`${i18n.t('APPLICATIONS_CLIENTS_COUNT_KEY')}: `}
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {licenseGroupDetails?.clients_count}
                </Box>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography className={classes.text} component="div">
                {`${i18n.t('UPDATED_AT_KEY')}: `}
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {dateFormatWithAt(licenseGroupDetails?.updated_at)}
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <Grid
        container
        justify="space-between"
        className={classes.editButton}
        alignItems="center"
      >
        <Typography className={classes.titleSection}>
          {i18n.t('CLIENTS_KEY')}
        </Typography>
        <Permission value="edit_license_group_clients">
          <Button
            className={classes.button}
            outlineBlue
            onClick={() => handleEditClientsModal()}
          >
            {i18n.t('EDIT_CLIENTS_KEY')}
          </Button>
        </Permission>
      </Grid>
      <Grid>
        <TableItems
          header={
            <>
              {headers.map(item => (
                <TableCell key={item?.key}>{i18n.t(item?.i18n)}</TableCell>
              ))}
            </>
          }
          renderItems={item => (
            <TableRow key={item?.uuid}>
              <TableCell className={classes.rowAllRowsClient}>
                {item?.name}
              </TableCell>
              <TableCell className={classes.rowAllRowsClient}>
                {item?.uuid}
              </TableCell>
            </TableRow>
          )}
          data={{
            count: 0,
            next: '',
            previous: '',
            results: licenseGroupDetails.clients,
          }}
          isLoading={isLoadingLicenseGroups}
          notFooter
          notFoundText={i18n.t('NO_CLIENT_KEY')}
        />
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  environments: { isLoadingLicenseGroups, licenseGroupDetails },
}: ApplicationState) => ({
  licenseGroupDetails,
  isLoadingLicenseGroups,
});

export default connect(mapStateToProps, {
  getLicenseGroupDetailsRequest,
  modalOpen,
})(DetailsLicenseGroup);
